import React, {useEffect, useState, useMemo, useRef} from 'react';
import {MenuItem} from '@blueprintjs/core';
import {IconNames} from '@blueprintjs/icons';
import {ItemRenderer, MultiSelect} from '@blueprintjs/select';

import {setInitialValue} from './utils';
import {Attachment} from './types';

interface SelectAttachmentsProps {
  value?: string[] | null;
  options: Attachment[];
  disabled: boolean;
  onChange: (value: any) => void;
}

const SelectElement = MultiSelect.ofType<Attachment>();

const renderItem: ItemRenderer<Attachment> = (item: Attachment, {handleClick, modifiers, query}: any) => {
  if (!modifiers.matchesPredicate) {
    return null;
  }

  return (
    <MenuItem
      text={item.value}
      key={item.unique_key || item.key}
      onClick={handleClick}/>
  )
};

export const createNewItemRenderer = (
  query: string,
  active: boolean,
  handleClick: React.MouseEventHandler<HTMLElement>,
) => (
  <MenuItem
    icon={IconNames.ADD}
    text={`Add "${query}"`}
    active={active}
    onClick={handleClick}
    shouldDismissPopover={false}
  />
);

const itemPredicate = (query: string, item: Attachment) => item.value.toLowerCase().includes(query.toLowerCase());

const tagRenderer = (item: Attachment) => item.value;

const createNewItemFromQuery = (query: string): Attachment => {
  return {
    key: null,
    value: query
  };
};

const SelectAttachments = ({
                  value,
                  disabled,
                  options,
                  onChange,
                }: SelectAttachmentsProps) => {
  const init = useRef(false);
  const initialValue = useMemo(() => setInitialValue(value, options), []);
  const [selectedItems, setSelectedItems] = useState<Attachment[]>(initialValue);
  const [filteredItems, setFilteredItems] = useState<Attachment[]>([]); // items that can be selected

  useEffect(() => {
    /*
     * `selectedItems` are used by reference;
     * `filteredItems` equal `options` without `selectedItems`
     */
    const keys = selectedItems.map((s: Attachment) => s.key);
    setFilteredItems(options.filter((o: Attachment) => !keys.includes(o.key)));
  }, [options?.length]);

  useEffect(() => {
    /*
     * If selected items change
     * - modify filteredItems
     * - trigger `onChange`
     */
    if (!init.current) {
      return;
    }

    const keys = selectedItems.map((s: Attachment) => s.key);
    const values = selectedItems.map((s: Attachment) => s.value);

    /*
     * `filteredItems` equal `options` without `selectedItems`
     *
     */
    setFilteredItems(options.filter((o: Attachment) => !keys.includes(o.key)));
    onChange(values);
  }, [selectedItems.length]);

  useEffect(() => {
    /*
     * In case of edit, `value` is provided
     */
    if (value?.length && !selectedItems.length) {
      const selectedItems: Attachment[] = [];

      value.forEach((v: string) => {
        const option = options.find((a: Attachment) => v.includes(a.value));

        if (option) {
          selectedItems.push(option)
        } else {
          /*
           * if value is not found it means it's a custom value;
           */
          selectedItems.push(createNewItemFromQuery(v))
        }
      })

      setSelectedItems(selectedItems); // this will trigger the other `useEffect`
    }
    if (!init.current) {
      init.current = true;
    }
  }, [value]);

  const onItemSelect = (item: Attachment) => {
    setSelectedItems((oldItems: Attachment[]) => [...oldItems, item]);
  };

  const onItemRemove = (item: Attachment) => {
    setSelectedItems(
      (oldItems: Attachment[]) =>
        [
          ...oldItems.filter((oldItem: Attachment) => oldItem.value !== item.value)
        ]
    );
  };

  const onItemsPaste = (items: Attachment[]) => {
    /*
     * Handle a bug.
     * This is invoked on paste:
     * - 1 BUG we're aware about if you paste a string that contains a `,`,
     *   the string is seperated into multiple items; we fix this case here
     */
    if (items.length > 1) {
      /*
       * This firstItem represents a bug fix, in case you paste an existing item,
       *   BP component partially matches it and selects it and adds the value, so you end up with
       *   `) }}` in the middle of the value
       */
      let firstItemValue = items[0].value.replace(/\) }}$/, '');

      let _value = items
        .map((a: Attachment, index) => index === 0 ? firstItemValue : a.value)
        .join(', ');
      onItemSelect(createNewItemFromQuery(_value));
    }
  };

  return (
    <SelectElement
      fill={true}
      items={filteredItems}
      itemPredicate={itemPredicate}
      selectedItems={selectedItems}
      itemRenderer={renderItem}
      onItemSelect={onItemSelect}
      onRemove={onItemRemove}
      resetOnSelect={true}
      tagRenderer={tagRenderer}
      createNewItemFromQuery={createNewItemFromQuery}
      createNewItemRenderer={createNewItemRenderer}
      onItemsPaste={onItemsPaste}
      popoverProps={{
        minimal: true,
        captureDismiss: true /* close only this popover */,
        portalClassName: 'select-attachments-portal',
        usePortal: true,
        // interactionKind: Popover2InteractionKind.CLICK,
      }}
      tagInputProps={{
        disabled: disabled,
      }}
    />
  )
};

export default SelectAttachments;
